import {
  Clock, FileDown, FileSpreadsheet, MapPin, Moon, Pencil, Plus, Route, Search, Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { atualizar, criar, eliminar, obter, paraQuery } from '../api';
import { useAuth } from '../auth';
import {
  Aviso, Carregando, Confirmacao, Erro, ListaComNovo, Modal, Spinner, Vazio,
} from '../componentes/base';
import { exportarRegistosExcel, exportarRegistosPdf } from '../exportar';
import {
  atravessaMeiaNoite, dataCurta, euros, hoje, horas, km, mesAtual, nomeMes, numero,
  numeroServicoDe,
} from '../formatos';
import type { Empresa, Local, Referencia, Registo, Totais } from '../tipos';

interface Resposta {
  registos: Registo[];
  totais: Totais;
  intervalo_ativo: boolean;
}

const FILTROS_VAZIOS = {
  empresa_id: '',
  funcao: '',
  funcionario_id: '',
  ano: '',
  mes: '',
  numero_servico: '',
  data_inicio: '',
  data_fim: '',
};

export default function RegistoHoras() {
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [resposta, setResposta] = useState<Resposta | null>(null);
  const [referencia, setReferencia] = useState<Referencia | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const [formAberto, setFormAberto] = useState(false);
  const [aEditar, setAEditar] = useState<Registo | null>(null);
  const [aEliminar, setAEliminar] = useState<Registo | null>(null);
  const [aProcessar, setAProcessar] = useState(false);

  const carregar = useCallback(() => {
    // Hold off until the reference data has told us which month to open on.
    if (!filtros.ano || !filtros.mes) return;
    setACarregar(true);
    setErro(null);
    obter<Resposta>(`/registos${paraQuery({ ...filtros, limite: 500 })}`)
      .then(setResposta)
      .catch((e) => setErro(e.message))
      .finally(() => setACarregar(false));
  }, [filtros]);

  useEffect(carregar, [carregar]);

  useEffect(() => {
    obter<Referencia>('/referencia')
      .then((r) => {
        setReferencia(r);
        // Open on the latest month with data rather than on today's, which is
        // almost always empty — timesheets arrive after the month they cover.
        const [ano, mes] = (r.ultimo_mes ?? '').split('-');
        if (ano && mes) {
          setFiltros((f) => (f.ano || f.mes ? f : { ...f, ano, mes: String(Number(mes)) }));
        }
      })
      .catch(() => setErro('Não foi possível carregar os dados de referência.'));
  }, []);

  const intervaloAtivo = Boolean(filtros.data_inicio || filtros.data_fim);

  const descricaoFiltro = useMemo(() => {
    if (intervaloAtivo) {
      return `${filtros.data_inicio || 'início'} a ${filtros.data_fim || 'hoje'}`;
    }
    return `${nomeMes(Number(filtros.mes))}-${filtros.ano}`;
  }, [filtros, intervaloAtivo]);

  async function apagar() {
    if (!aEliminar) return;
    setAProcessar(true);
    try {
      await eliminar(`/registos/${aEliminar.id}`);
      setAEliminar(null);
      carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível eliminar.');
    } finally {
      setAProcessar(false);
    }
  }

  const registos = resposta?.registos ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Clock className="h-6 w-6 text-marca-600 dark:text-marca-400" aria-hidden />
          <h1 className="text-xl font-semibold">Registo de Horas</h1>
        </div>
        <button
          type="button"
          className="btn-primario sem-impressao"
          onClick={() => { setAEditar(null); setFormAberto(true); }}
        >
          <Plus className="h-4 w-4" aria-hidden />
          Novo Registo
        </button>
      </div>

      <section className="cartao sem-impressao p-4" aria-label="Filtros">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label htmlFor="f-empresa" className="rotulo">Fornecedor</label>
            <select
              id="f-empresa" className="campo" value={filtros.empresa_id}
              onChange={(e) => setFiltros((f) => ({ ...f, empresa_id: e.target.value }))}
            >
              <option value="">Todos os fornecedores</option>
              {referencia?.empresas.map((e) => (
                <option key={e.id} value={e.id}>{e.nome}</option>
              ))}
              <option value="sem-empresa">— Sem fornecedor —</option>
            </select>
          </div>

          <div>
            <label htmlFor="f-funcao" className="rotulo">Função</label>
            <select
              id="f-funcao" className="campo" value={filtros.funcao}
              onChange={(e) => setFiltros((f) => ({ ...f, funcao: e.target.value }))}
            >
              <option value="">Todas as funções</option>
              {referencia?.funcoes.map((f) => (
                <option key={f.codigo} value={f.codigo}>{f.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="f-funcionario" className="rotulo">Funcionário</label>
            <select
              id="f-funcionario" className="campo" value={filtros.funcionario_id}
              onChange={(e) => setFiltros((f) => ({ ...f, funcionario_id: e.target.value }))}
            >
              <option value="">Todos os funcionários</option>
              {referencia?.funcionarios.map((f) => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="f-ano" className="rotulo">Ano</label>
            <select
              id="f-ano" className="campo" value={filtros.ano} disabled={intervaloAtivo}
              onChange={(e) => setFiltros((f) => ({ ...f, ano: e.target.value }))}
            >
              {Array.from(new Set([
                ...(referencia?.anos ?? []),
                new Date().getFullYear(),
                new Date().getFullYear() - 1,
              ])).sort((a, b) => b - a).map((ano) => (
                <option key={ano} value={ano}>{ano}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="f-mes" className="rotulo">Mês</label>
            <select
              id="f-mes" className="campo" value={filtros.mes} disabled={intervaloAtivo}
              onChange={(e) => setFiltros((f) => ({ ...f, mes: e.target.value }))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{nomeMes(m)}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="f-servico" className="rotulo">Nº Serviço</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-400" aria-hidden />
              <input
                id="f-servico" className="campo pl-9" placeholder="Pesquisar por nº de serviço…"
                value={filtros.numero_servico}
                onChange={(e) => setFiltros((f) => ({ ...f, numero_servico: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <fieldset className="mt-4 border-t border-papel-200 pt-4 dark:border-tinta-700">
          <legend className="sr-only">Filtro por intervalo de datas</legend>
          <p className="mb-2 text-sm font-medium text-tinta-700 dark:text-papel-300">
            Filtro por Intervalo de Datas
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label htmlFor="f-inicio" className="rotulo">Data Início</label>
              <input
                id="f-inicio" type="date" className="campo" value={filtros.data_inicio}
                onChange={(e) => setFiltros((f) => ({ ...f, data_inicio: e.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="f-fim" className="rotulo">Data Fim</label>
              <input
                id="f-fim" type="date" className="campo" value={filtros.data_fim}
                onChange={(e) => setFiltros((f) => ({ ...f, data_fim: e.target.value }))}
              />
            </div>
          </div>
          {intervaloAtivo && (
            <button
              type="button"
              className="mt-2 text-sm font-medium text-marca-600 hover:underline dark:text-marca-400"
              onClick={() => setFiltros((f) => ({ ...f, data_inicio: '', data_fim: '' }))}
            >
              Limpar filtro de datas
            </button>
          )}
          <p className="mt-1 text-xs text-tinta-500 dark:text-tinta-400">
            Nota: o filtro por intervalo de datas substitui os filtros de ano/mês quando ativo.
          </p>
        </fieldset>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button" className="btn bg-emerald-600 text-white hover:bg-emerald-700"
            disabled={!registos.length}
            onClick={() => resposta && exportarRegistosExcel(registos, resposta.totais, descricaoFiltro)}
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
            Exportar Excel
          </button>
          <button
            type="button" className="btn bg-red-600 text-white hover:bg-red-700"
            disabled={!registos.length}
            onClick={() => resposta && exportarRegistosPdf(registos, resposta.totais, descricaoFiltro)}
          >
            <FileDown className="h-4 w-4" aria-hidden />
            Exportar PDF
          </button>
        </div>
      </section>

      {erro && <Erro mensagem={erro} aoTentar={carregar} />}

      {resposta && registos.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[
            { rotulo: 'Registos', valor: numero(resposta.totais.n_registos) },
            { rotulo: 'Funcionários', valor: numero(resposta.totais.n_funcionarios) },
            { rotulo: 'Serviços', valor: numero(resposta.totais.n_servicos) },
            { rotulo: 'Total de horas', valor: horas(resposta.totais.total_horas) },
            { rotulo: 'Custo estimado', valor: euros(resposta.totais.custo_total) },
          ].map((item) => (
            <div key={item.rotulo} className="cartao p-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-tinta-500 dark:text-tinta-400">
                {item.rotulo}
              </p>
              <p className="mt-1 text-lg font-semibold tabular-nums">{item.valor}</p>
            </div>
          ))}
        </div>
      )}

      <div className="cartao overflow-hidden">
        {aCarregar ? (
          <Carregando />
        ) : !registos.length ? (
          <Vazio
            titulo="Nenhum registo encontrado"
            descricao="Ajuste os filtros ou crie um novo registo."
            acao={
              <button type="button" className="btn-primario" onClick={() => { setAEditar(null); setFormAberto(true); }}>
                <Plus className="h-4 w-4" aria-hidden />Novo Registo
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-papel-50 text-left text-[11px] uppercase tracking-wide text-tinta-500 dark:bg-tinta-700/50 dark:text-tinta-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Funcionário</th>
                  <th className="px-4 py-3 font-medium">Data</th>
                  <th className="px-4 py-3 font-medium">Horário</th>
                  <th className="px-4 py-3 text-right font-medium">Total de horas</th>
                  <th className="px-4 py-3 font-medium">Local</th>
                  <th className="px-4 py-3 text-right font-medium">Km</th>
                  <th className="px-4 py-3 font-medium">Nº Serviço</th>
                  <th className="px-4 py-3 font-medium">Fornecedor</th>
                  <th className="px-4 py-3 text-right font-medium sem-impressao">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-papel-200 dark:divide-tinta-700">
                {registos.map((r) => (
                  <tr key={r.id} className="hover:bg-papel-50 dark:hover:bg-tinta-700/40">
                    <td className="px-4 py-3">
                      <p className="font-medium">{r.funcionario_nome}</p>
                      <p className="text-xs text-tinta-500 dark:text-tinta-400">{r.funcao_nome ?? r.funcao}</p>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-tinta-600 dark:text-papel-300">
                      {dataCurta(r.data)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 tabular-nums text-tinta-600 dark:text-papel-300">
                        <Clock className="h-3.5 w-3.5 text-tinta-400" aria-hidden />
                        {r.hora_inicio} - {r.hora_fim}
                        {atravessaMeiaNoite(r.hora_inicio, r.hora_fim) && (
                          <Moon
                            className="h-3.5 w-3.5 text-marca-500"
                            aria-label="Turno que atravessa a meia-noite"
                          />
                        )}
                      </span>
                      {r.pausa_minutos > 0 && (
                        <p className="text-xs text-tinta-400">pausa {r.pausa_minutos} min</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {horas(r.total_horas)}
                      {r.horas_extra > 0 && (
                        <p className="text-xs font-normal text-orange-600 dark:text-orange-400">
                          {horas(r.horas_extra)} extra
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-tinta-600 dark:text-papel-300">
                      {r.local_nome ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 text-tinta-400" aria-hidden />{r.local_nome}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-tinta-600 dark:text-papel-300">
                      <span className="inline-flex items-center gap-1">
                        <Route className="h-3.5 w-3.5 text-tinta-400" aria-hidden />{km(r.km)}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-tinta-600 dark:text-papel-300">
                      {r.numero_servico ?? <span className="text-tinta-400">sem nº</span>}
                    </td>
                    <td className="px-4 py-3">
                      {r.empresa_nome ? (
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          r.empresa_tipo === 'interno'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : 'bg-papel-200 text-tinta-700 dark:bg-tinta-700 dark:text-papel-300'
                        }`}>
                          {r.empresa_nome}
                        </span>
                      ) : <span className="text-tinta-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right sem-impressao">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-marca-600 hover:bg-marca-50 dark:text-marca-400 dark:hover:bg-tinta-700"
                          onClick={() => { setAEditar(r); setFormAberto(true); }}
                          aria-label={`Editar registo de ${r.funcionario_nome}`}
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-tinta-700"
                          onClick={() => setAEliminar(r)}
                          aria-label={`Eliminar registo de ${r.funcionario_nome}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formAberto && referencia && (
        <FormularioRegisto
          referencia={referencia}
          registo={aEditar}
          aoFechar={() => setFormAberto(false)}
          aoGuardar={() => { setFormAberto(false); carregar(); }}
          aoCriarLocal={(local) => setReferencia((r) => (r ? {
            ...r,
            locais: [...r.locais, local].sort((a, b) => a.nome.localeCompare(b.nome, 'pt')),
          } : r))}
          aoCriarEmpresa={(empresa) => setReferencia((r) => (r ? {
            ...r,
            empresas: [...r.empresas, empresa].sort((a, b) => a.nome.localeCompare(b.nome, 'pt')),
          } : r))}
        />
      )}

      <Confirmacao
        aberto={Boolean(aEliminar)}
        titulo="Eliminar registo"
        mensagem={aEliminar
          ? `Eliminar o registo de ${aEliminar.funcionario_nome} em ${dataCurta(aEliminar.data, true)} (${aEliminar.hora_inicio}–${aEliminar.hora_fim})? Fica guardado na auditoria e pode ser recuperado.`
          : ''}
        aCarregar={aProcessar}
        aoConfirmar={apagar}
        aoCancelar={() => setAEliminar(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function FormularioRegisto({
  referencia, registo, aoFechar, aoGuardar, aoCriarLocal, aoCriarEmpresa,
}: {
  referencia: Referencia;
  registo: Registo | null;
  aoFechar: () => void;
  aoGuardar: () => void;
  aoCriarLocal: (local: Local) => void;
  aoCriarEmpresa: (empresa: Empresa) => void;
}) {
  const aEditar = Boolean(registo);
  const { utilizador } = useAuth();
  const admin = utilizador?.papel === 'admin';

  const [funcionarios, setFuncionarios] = useState<string[]>(
    registo ? [registo.funcionario_id] : [],
  );
  const [form, setForm] = useState({
    data: registo?.data ?? hoje(),
    hora_inicio: registo?.hora_inicio ?? '',
    hora_fim: registo?.hora_fim ?? '',
    pausa_minutos: String(registo?.pausa_minutos ?? 0),
    horas_extra: String(registo?.horas_extra ?? 0),
    empresa_id: registo?.empresa_id ?? '',
    local_id: registo?.local_id ?? '',
    km: String(registo?.km ?? 0),
    numero_servico: registo?.numero_servico ?? '',
    notas: registo?.notas ?? '',
  });
  const [erro, setErro] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [aGuardar, setAGuardar] = useState(false);

  // Live preview of the computed total, so a mistyped time is obvious before
  // saving rather than after.
  const previsao = useMemo(() => {
    const valida = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!valida.test(form.hora_inicio) || !valida.test(form.hora_fim)) return null;
    const emHoras = (v: string) => Number(v.slice(0, 2)) + Number(v.slice(3)) / 60;
    let bruto = (emHoras(form.hora_fim) - emHoras(form.hora_inicio) + 24) % 24;
    if (bruto === 0) bruto = 24;
    const total = bruto - (Number(form.pausa_minutos) || 0) / 60;
    return {
      total,
      meiaNoite: atravessaMeiaNoite(form.hora_inicio, form.hora_fim),
      invalido: total <= 0,
    };
  }, [form.hora_inicio, form.hora_fim, form.pausa_minutos]);

  // What the date would normally be numbered as — a suggestion to click, not a
  // value that gets applied by leaving the field empty.
  const numeroSugerido = form.data ? numeroServicoDe(form.data) : '';

  async function submeter(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setAvisos([]);

    if (!funcionarios.length) {
      setErro('Selecione pelo menos um funcionário.');
      return;
    }

    setAGuardar(true);
    const corpo = {
      data: form.data,
      hora_inicio: form.hora_inicio,
      hora_fim: form.hora_fim,
      pausa_minutos: Number(form.pausa_minutos) || 0,
      horas_extra: Number(form.horas_extra) || 0,
      empresa_id: form.empresa_id || null,
      local_id: form.local_id || null,
      km: Number(form.km) || 0,
      numero_servico: form.numero_servico || undefined,
      notas: form.notas || null,
    };

    try {
      if (aEditar && registo) {
        const r = await atualizar<{ avisos: string[] }>(`/registos/${registo.id}`, {
          ...corpo, funcionario_id: funcionarios[0],
        });
        if (r.avisos?.length) { setAvisos(r.avisos); setAGuardar(false); return; }
      } else {
        const r = await criar<{ avisos: string[] }>('/registos', {
          ...corpo, funcionario_ids: funcionarios,
        });
        if (r.avisos?.length) { setAvisos(r.avisos); setAGuardar(false); setTimeout(aoGuardar, 1800); return; }
      }
      aoGuardar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível guardar.');
    } finally {
      setAGuardar(false);
    }
  }

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={aEditar ? 'Editar Registo' : 'Novo Registo'}
      largura="max-w-3xl"
    >
      <form onSubmit={submeter} className="space-y-4">
        {erro && <Erro mensagem={erro} />}
        {avisos.map((aviso) => <Aviso key={aviso}>{aviso}</Aviso>)}

        <div>
          <span className="rotulo">
            {aEditar ? 'Funcionário' : 'Funcionários'}
            {!aEditar && (
              <span className="ml-2 font-normal text-tinta-500">
                — selecione vários para lançar a mesma escala de uma vez
              </span>
            )}
          </span>
          {aEditar ? (
            <select
              className="campo" value={funcionarios[0] ?? ''}
              onChange={(e) => setFuncionarios([e.target.value])}
            >
              {referencia.funcionarios.map((f) => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          ) : (
            <>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-papel-300 p-2 dark:border-tinta-700">
                {referencia.funcionarios.map((f) => (
                  <label key={f.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-papel-50 dark:hover:bg-tinta-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-papel-300 text-marca-600 focus:ring-marca-500"
                      checked={funcionarios.includes(f.id)}
                      onChange={(e) => setFuncionarios((atual) =>
                        e.target.checked ? [...atual, f.id] : atual.filter((id) => id !== f.id))}
                    />
                    <span>{f.nome}</span>
                    <span className="text-xs text-tinta-400">{f.funcao_nome}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-tinta-500 dark:text-tinta-400">
                {funcionarios.length} selecionado(s)
              </p>
            </>
          )}
        </div>

        <ListaComNovo<Empresa>
          id="r-empresa"
          rotulo="Fornecedor"
          opcoes={referencia.empresas}
          valor={form.empresa_id}
          textoVazio="— O habitual do funcionário —"
          aoEscolher={(id) => setForm((f) => ({ ...f, empresa_id: id }))}
          podeAdicionar={admin}
          rotuloNovo="+ Novo fornecedor"
          placeholderNovo="Nome do fornecedor"
          extra={{
            tipo: 'escolha',
            rotulo: 'Tipo',
            opcoes: [{ valor: 'agencia', texto: 'Agência' }, { valor: 'interno', texto: 'Interno' }],
          }}
          aoCriar={async (nome, tipo) => {
            const { empresa } = await criar<{ empresa: Empresa }>('/referencia/empresas', {
              nome, tipo,
            });
            return empresa;
          }}
          aoCriado={aoCriarEmpresa}
          etiqueta={(e) => (e.tipo === 'interno' ? ' (interno)' : '')}
          ajuda={
            'Em branco, o turno conta para o fornecedor habitual do funcionário. '
            + 'Escolha aqui quando este turno foi prestado por outro — '
            + 'é o que separa as duas tabelas de fornecedor no fecho do mês.'
          }
        />

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="r-data" className="rotulo">Data</label>
            <input
              id="r-data" type="date" required className="campo" value={form.data}
              onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="r-inicio" className="rotulo">Hora de início</label>
            <input
              id="r-inicio" type="time" required className="campo" value={form.hora_inicio}
              onChange={(e) => setForm((f) => ({ ...f, hora_inicio: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="r-fim" className="rotulo">Hora de fim</label>
            <input
              id="r-fim" type="time" required className="campo" value={form.hora_fim}
              onChange={(e) => setForm((f) => ({ ...f, hora_fim: e.target.value }))}
            />
          </div>
        </div>

        {previsao && (
          <div className={`rounded-lg px-3 py-2 text-sm ${
            previsao.invalido
              ? 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'
              : 'bg-papel-50 text-tinta-700 dark:bg-tinta-700 dark:text-papel-200'
          }`}>
            {previsao.invalido ? (
              'A pausa é igual ou superior à duração do turno.'
            ) : (
              <>
                Total: <strong className="tabular-nums">{horas(previsao.total)}</strong>
                {previsao.meiaNoite && (
                  <span className="ml-2 inline-flex items-center gap-1 text-marca-600 dark:text-marca-400">
                    <Moon className="h-3.5 w-3.5" aria-hidden />
                    turno que atravessa a meia-noite
                  </span>
                )}
              </>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="r-pausa" className="rotulo">Pausa (min)</label>
            <input
              id="r-pausa" type="number" min="0" step="5" className="campo" value={form.pausa_minutos}
              onChange={(e) => setForm((f) => ({ ...f, pausa_minutos: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="r-extra" className="rotulo">Horas extra</label>
            <input
              id="r-extra" type="number" min="0" step="0.5" className="campo" value={form.horas_extra}
              onChange={(e) => setForm((f) => ({ ...f, horas_extra: e.target.value }))}
            />
          </div>
          <ListaComNovo<Local>
            id="r-local"
            rotulo="Local"
            opcoes={referencia.locais}
            valor={form.local_id}
            textoVazio="Sem local"
            aoEscolher={(id, local) => setForm((f) => ({
              ...f,
              local_id: id,
              // Pre-fill the round trip from the venue's known distance,
              // but only while the field is still untouched.
              km: (!Number(f.km) && local?.km_base) ? String(local.km_base * 2) : f.km,
            }))}
            podeAdicionar={admin}
            rotuloNovo="+ Novo local"
            placeholderNovo="Nome do local"
            extra={{ tipo: 'numero', rotulo: 'Km' }}
            aoCriar={async (nome, kmBase) => {
              const { local } = await criar<{ local: Local }>('/referencia/locais', {
                nome, km_base: kmBase.trim() === '' ? null : Number(kmBase),
              });
              return local;
            }}
            aoCriado={aoCriarLocal}
          />
          <div>
            <label htmlFor="r-km" className="rotulo">Km</label>
            <input
              id="r-km" type="number" min="0" step="1" className="campo" value={form.km}
              onChange={(e) => setForm((f) => ({ ...f, km: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="r-servico" className="rotulo">Nº Serviço</label>
            <input
              id="r-servico" className="campo" value={form.numero_servico}
              placeholder="AAAAMMDD"
              onChange={(e) => setForm((f) => ({ ...f, numero_servico: e.target.value }))}
            />
            {/* Offered, never applied on the user's behalf: left blank the
                registo is saved without a number rather than given a derived
                one the business never issued. */}
            <p className="mt-1 text-xs text-tinta-500 dark:text-tinta-400">
              {numeroSugerido ? (
                <>
                  Sugestão para esta data:{' '}
                  <button
                    type="button"
                    className="font-medium tabular-nums text-marca-600 underline dark:text-marca-400"
                    onClick={() => setForm((f) => ({ ...f, numero_servico: numeroSugerido }))}
                  >
                    {numeroSugerido}
                  </button>
                  {' '}— para um segundo serviço no mesmo dia use {numeroSugerido}-2.
                </>
              ) : 'Formato AAAAMMDD; para um segundo serviço no mesmo dia, AAAAMMDD-2.'}
              {' '}Em branco fica sem número.
            </p>
          </div>
          <div>
            <label htmlFor="r-notas" className="rotulo">Notas</label>
            <input
              id="r-notas" className="campo" value={form.notas}
              onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-papel-200 pt-4 dark:border-tinta-700">
          <button type="button" className="btn-secundario" onClick={aoFechar} disabled={aGuardar}>
            Cancelar
          </button>
          <button type="submit" className="btn-primario" disabled={aGuardar || previsao?.invalido}>
            {aGuardar && <Spinner classe="h-4 w-4" />}
            {aEditar ? 'Guardar alterações' : `Criar ${funcionarios.length || ''} registo(s)`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
